import { useMutation, useQuery } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { generalConfigApi } from '../../../apis';
import { CreateOptionDto, CreateOptionDtoCategoryEnum, Option, UpdateOptionDto } from '../../../apis/client-axios';
import ModalWrap, { ModalWrapType } from '../../../components/ModalWrap';
import ToastError from '../../../components/Toast/ToastError';
import ToastSuccess from '../../../components/Toast/ToastSuccess';
import Widget from '../../../components/Widget';
import { QUERY_KEY } from '../../../constants/QueryKey';
import Category from './Category';
import './style.scss';
import UpsertOptionModal from './UpsertOptionModal';

export interface OptionModal {
  type?: ModalWrapType;
  option?: Option;
}

const ChoiceSettings = () => {
  const intl = useIntl();

  const [optionModal, setOptionModal] = useState<OptionModal>();

  const { data: categoriesDataResponse, isLoading: isCategoryLoading } = useQuery({
    queryKey: [QUERY_KEY.CATEGORY],
    queryFn: () => generalConfigApi.generalConfigControllerGetOptionCategories(),
  });

  const {
    data: optionsDataResponse,
    isLoading: isOptionsLoading,
    refetch: refetchOptions,
  } = useQuery({
    queryKey: [QUERY_KEY.OPTIONS_SETTING],
    queryFn: () => generalConfigApi.generalConfigControllerGetOptions(),
  });

  const createOptionMutation = useMutation({
    mutationFn: (createOptionsDto: CreateOptionDto) =>
      generalConfigApi.generalConfigControllerCreateOption(createOptionsDto),
    onSuccess: () => {
      refetchOptions();

      if (optionModal) setOptionModal(undefined);

      ToastSuccess({
        content: intl.formatMessage({ id: 'choiceSettings.add.success' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'choiceSettings.add.fail' }),
      });
    },
  });

  const updateOptionMutation = useMutation({
    mutationFn: ({ id, updateOptionDto }: { id: string; updateOptionDto: UpdateOptionDto }) =>
      generalConfigApi.generalConfigControllerUpdateOptionById(id, updateOptionDto),
    onSuccess: () => {
      refetchOptions();

      if (optionModal) setOptionModal(undefined);

      ToastSuccess({
        content: intl.formatMessage({ id: 'choiceSettings.edit.success' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'choiceSettings.edit.fail' }),
      });
    },
  });

  const deleteOptionMutation = useMutation({
    mutationFn: (id: string) => generalConfigApi.generalConfigControllerDeleteOptionById(id),
    onSuccess: () => {
      refetchOptions();

      if (optionModal) setOptionModal(undefined);

      ToastSuccess({
        content: intl.formatMessage({ id: 'choiceSettings.delete.success' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'choiceSettings.delete.fail' }),
      });
    },
  });

  const categories = categoriesDataResponse?.data?.data;
  const options = optionsDataResponse?.data?.data;
  const loading = isCategoryLoading || isOptionsLoading;

  const handleCloseModal = () => {
    setOptionModal(undefined);
  };

  const handleUpsertOption = (values: CreateOptionDto | UpdateOptionDto) => {
    if (optionModal?.type === 'upsert') {
      if (!optionModal?.option?.id) {
        createOptionMutation.mutate({
          ...(values as CreateOptionDto),
          category: optionModal?.option?.category || CreateOptionDtoCategoryEnum.CheckIn,
        });
      } else {
        updateOptionMutation.mutate({ id: optionModal?.option?.id, updateOptionDto: { ...values } });
      }
    }
  };

  const handleDeleteOption = () => {
    if (optionModal?.type === 'delete' && optionModal?.option?.id) {
      deleteOptionMutation.mutate(optionModal?.option?.id);
    }
  };

  return (
    <>
      <Widget
        breadcrumbs={[intl.formatMessage({ id: 'adminMenu.choiceSettings' })]}
        title={intl.formatMessage({ id: 'adminMenu.choiceSettings' })}
        content={
          <Row gutter={[16, 16]} className="options-setting">
            {categories?.map((category) => {
              const _options = options?.filter((option) => option.category === category.code) || [];

              return (
                <Col key={category.code} span={24} className="options-setting-item">
                  <Category
                    loading={loading}
                    category={category}
                    options={_options}
                    onUpdateOption={(payload) => updateOptionMutation.mutate(payload)}
                    onOpenModal={(payload) => setOptionModal(payload)}
                  />
                </Col>
              );
            })}
          </Row>
        }
      />

      {optionModal?.type === 'upsert' && (
        <UpsertOptionModal
          loading={createOptionMutation.isPending || updateOptionMutation.isPending}
          optionModal={optionModal}
          onSubmit={handleUpsertOption}
          onCancel={handleCloseModal}
        />
      )}

      {optionModal?.type === 'delete' && (
        <ModalWrap
          open={optionModal?.type === 'delete'}
          onSubmit={handleDeleteOption}
          onCancel={handleCloseModal}
          title={intl.formatMessage({ id: 'choiceSettings.delete' })}
          cancelText={intl.formatMessage({ id: 'common.cancel' })}
          submitText={intl.formatMessage({ id: 'common.remove' })}
          buttonSubmitProps={{ disabled: deleteOptionMutation.isPending }}
          type={optionModal.type}
          body={
            <Row align="middle" justify="center">
              <Col className="options-setting-modal-delete-title">
                <span>{optionModal?.option?.title}</span>
                {intl.formatMessage({ id: 'choiceSettings.confirmDelete' })}
              </Col>
            </Row>
          }
        />
      )}
    </>
  );
};

export default ChoiceSettings;
